/**
 * #897 — Audio pipeline warm-up helpers.
 *
 * Two responsibilities, kept together because they're both first-turn
 * specific:
 *
 *   createWarmupTimings()
 *     Lightweight per-call stopwatch. Records millisecond offsets
 *     (relative to a `t0` mark) for named events along the audio
 *     bring-up path: getUserMedia resolve, Deepgram (voice WS) open,
 *     first PCM/MediaRecorder chunk dispatched, first Deepgram partial
 *     transcript received. At end-of-call the owner calls .dump()
 *     which prints a single `[warmup]` console.log line — captured by
 *     the existing #718 console-buffer plumbing and persisted to
 *     `client_console_logs.entries`. No DB-schema migration needed.
 *     The next iteration of this work (TBD, see issue #897 ac
 *     "Diagnose") will sweep the per-call rows to identify the
 *     dominant warm-up cost on real tablets.
 *
 *     Each named event is recorded EXACTLY ONCE — subsequent .mark()
 *     calls for the same name are ignored. This matches the
 *     "first-X-after-call-start" semantics we care about.
 *
 *   isFirstChunkSilent(bytes, opts)
 *     Belt-and-braces guard against the browser `getUserMedia` cold
 *     start that the issue calls out as the most likely click source:
 *     the first ~100ms of mic frames sometimes carry DC offset / zeros
 *     before the audio graph stabilises. This function inspects a raw
 *     16-bit signed LE PCM buffer and returns true when ≥`thresholdMs`
 *     of its leading bytes are at-or-below the noise floor at the
 *     declared sample rate. The caller (server-side audio_chunk
 *     handler) drops the chunk when this returns true rather than
 *     forwarding the silent frame to Deepgram. Counters around the
 *     call site let us monitor how often this fires.
 *
 *     Pure / side-effect-free / fully unit-testable. The server-side
 *     equivalent lives in `app/services/audio_warmup.py` but the
 *     algorithm is the same — kept here too so the client (which
 *     could later perform the same guard before sending) has a
 *     local copy.
 */

const WARMUP_LOG_PREFIX = '[warmup]';

/**
 * The named events we capture for warm-up timing. Order = the
 * approximate sequence we expect them to fire in on a healthy call.
 * Listed explicitly so the .dump() output has a stable shape and a
 * typo in a .mark() call from TabletApp.js doesn't silently extend
 * the schema.
 */
export const WARMUP_EVENT_NAMES = Object.freeze([
  // Call lifecycle anchors.
  't0',                        // handleCall fired (user-gesture tap)
  'ws_open',                   // voice WS readyState=OPEN
  // Pre-warm path (#897 — runs during the "Berta is connecting…" UI).
  'getUserMedia_resolved',     // navigator.mediaDevices.getUserMedia() resolved
  'pcm_audio_context_ready',   // createPcmAudioContext() resolved (state=running)
  // Per-call STT-side first-frame timing.
  'first_audio_chunk',         // first mic chunk dispatched onto the send chain
  'first_stt_partial',         // first 'partial_transcript' WS event received
]);

/**
 * Create a per-call timings collector. Caller threads the same
 * instance through handleCall / mark callbacks / cleanup.
 *
 * The returned object is intentionally tiny — keep this stable
 * because TabletApp.js holds it via useRef across the call's full
 * lifecycle.
 */
export function createWarmupTimings(now = () => Date.now()) {
  // t0 is the reference point; we record absolute ms-since-epoch on
  // construction and report all other marks as deltas from t0.
  const t0 = now();
  /** @type {Object<string, number>} */
  const marks = { t0: 0 };

  function mark(name) {
    if (typeof name !== 'string' || !name) return;
    if (!WARMUP_EVENT_NAMES.includes(name)) {
      // Unknown — likely a typo. Don't pollute the marks dictionary.
      // Surface via console.warn so QA spots it during dev / staging.
      console.warn(`${WARMUP_LOG_PREFIX} ignoring unknown event name: ${name}`);
      return;
    }
    if (name === 't0') return; // t0 set at construction, not mutable
    if (marks[name] !== undefined) return; // first wins
    marks[name] = now() - t0;
  }

  function snapshot() {
    return { ...marks };
  }

  function dump(extra) {
    // One structured line — picked up by the #718 console-capture
    // ring buffer + flushed into `client_console_logs.entries` at
    // end-of-call. The shape is `{event, ...}` so a future grep or
    // SQL extraction can filter on `event:'warmup'` without parsing
    // the whole line.
    const payload = {
      event: 'warmup',
      timings_ms: snapshot(),
      ...(extra || {}),
    };
    try {
      console.log(`${WARMUP_LOG_PREFIX} ${JSON.stringify(payload)}`);
    } catch (_) {
      // Stringify failure is non-fatal — diagnostic only.
    }
    return payload;
  }

  return { mark, snapshot, dump };
}

/**
 * Detect whether the leading `thresholdMs` of a raw 16-bit signed
 * little-endian PCM buffer is at-or-below the silence floor.
 *
 * Used server-side as a first-turn safety net (`turn_idx === 1`,
 * see app/services/audio_warmup.py). The browser `getUserMedia` cold
 * start can produce a short stretch of DC-offset / near-zero samples
 * before the audio graph stabilises — these add nothing for STT and
 * cause the audible click when later played back through the
 * recording diagnostics page.
 *
 * Parameters:
 *   bytes        — ArrayBuffer | Uint8Array | Node Buffer of PCM samples
 *   opts.sampleRate    — sample rate in Hz (e.g. 48000)
 *   opts.thresholdMs   — duration of leading silence required (default 80ms)
 *   opts.silenceAbs    — absolute sample magnitude considered silent
 *                        (default 64 — DC offset on tablet mics rarely
 *                        exceeds this; real speech RMS sits in the
 *                        thousands).
 *
 * Returns: true when the first `thresholdMs` of audio is silent, OR
 *          the buffer is shorter than `thresholdMs` AND is entirely
 *          silent. Returns false otherwise. Returns false on invalid
 *          inputs (defensive — STT path must never break).
 */
export function isFirstChunkSilent(bytes, opts = {}) {
  // #1036 — use ?? not || so an explicit invalid `sampleRate: 0` is
  // rejected by the guard below rather than silently coerced to 48000
  // (matches the thresholdMs / silenceAbs defaults and the documented
  // "returns false on invalid inputs" contract).
  const sampleRate = opts.sampleRate ?? 48000;
  const thresholdMs = opts.thresholdMs ?? 80;
  const silenceAbs = opts.silenceAbs ?? 64;
  if (!bytes) return false;
  if (typeof sampleRate !== 'number' || sampleRate <= 0) return false;
  if (typeof thresholdMs !== 'number' || thresholdMs <= 0) return false;

  // Normalise input to Int16Array view (assumes 16-bit signed LE).
  let i16;
  try {
    if (bytes instanceof Int16Array) {
      i16 = bytes;
    } else if (bytes instanceof ArrayBuffer) {
      i16 = new Int16Array(bytes);
    } else if (ArrayBuffer.isView(bytes)) {
      // Uint8Array / Buffer / DataView wrapper — re-view as Int16.
      // Require an even byte length, else the trailing odd byte would
      // be dropped silently. Pad-down rather than fail noisily.
      const byteLen = bytes.byteLength - (bytes.byteLength % 2);
      i16 = new Int16Array(bytes.buffer, bytes.byteOffset, byteLen / 2);
    } else {
      return false;
    }
  } catch (_) {
    return false;
  }

  const samplesNeeded = Math.ceil((sampleRate * thresholdMs) / 1000);
  const samplesToCheck = Math.min(samplesNeeded, i16.length);
  if (samplesToCheck <= 0) return false;
  for (let i = 0; i < samplesToCheck; i++) {
    const v = i16[i];
    const abs = v < 0 ? -v : v;
    if (abs > silenceAbs) return false;
  }
  // If we requested 80ms but only got 30ms of samples in this chunk
  // AND all of them were silent, treat as silent — typical when the
  // mic ships extremely short first chunks during cold start.
  return true;
}

export const __testing__ = {
  WARMUP_LOG_PREFIX,
};
